import { api } from './api'

export interface Webhook {
  id: string
  chatbot_id: string
  url: string
  events: string[]
  enabled: boolean
  failure_count: number
  created_at: string | null
}

export interface WebhookCreateResponse extends Webhook {
  secret: string
}

export interface WebhookCreateRequest {
  url: string
  events: string[]
}

export interface DeliveryLog {
  id: string
  webhook_id: string
  event_type: string
  status: string
  http_status: number | null
  attempt: number
  error_message: string | null
  payload_preview: string | null
  created_at: string | null
}

export interface WebhookStats {
  total_webhooks: number
  max_webhooks: number
  successful_deliveries: number
  failed_deliveries: number
}

export interface WebhookTestRequest {
  event_type: string
}

export interface WebhookTestResponse {
  success: boolean
  http_status: number | null
  error_message: string | null
  delivery_log_id: string
  attempt?: number
}

export const SUPPORTED_EVENTS = [
  'conversation.started',
  'conversation.ended',
  'handoff.triggered',
  'handoff.resolved',
  'lead.captured',
  'message.received',
  'training.started',
  'training.completed',
  'training.failed',
] as const

export const webhooksService = {
  async list(chatbotId: string): Promise<Webhook[]> {
    const { data } = await api.get<Webhook[]>(`/chatbots/${chatbotId}/webhooks`)
    return data
  },

  async create(chatbotId: string, body: WebhookCreateRequest): Promise<WebhookCreateResponse> {
    const { data } = await api.post<WebhookCreateResponse>(`/chatbots/${chatbotId}/webhooks`, body)
    return data
  },

  async toggle(webhookId: string, enabled: boolean): Promise<Webhook> {
    const { data } = await api.patch<Webhook>(`/webhooks/${webhookId}`, { enabled })
    return data
  },

  async remove(webhookId: string): Promise<void> {
    await api.delete(`/webhooks/${webhookId}`)
  },

  async logs(chatbotId: string): Promise<DeliveryLog[]> {
    const { data } = await api.get<DeliveryLog[]>(`/chatbots/${chatbotId}/webhook-logs`)
    return data
  },

  async retry(logId: string): Promise<WebhookTestResponse> {
    const { data } = await api.post<WebhookTestResponse>(`/webhook-logs/${logId}/retry`)
    return data
  },

  async stats(chatbotId: string): Promise<WebhookStats> {
    const { data } = await api.get<WebhookStats>(`/chatbots/${chatbotId}/webhook-stats`)
    return data
  },

  async test(webhookId: string, body: WebhookTestRequest): Promise<WebhookTestResponse> {
    const { data } = await api.post<WebhookTestResponse>(`/webhooks/${webhookId}/test`, body)
    return data
  },
}
